import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import LabPreferenceSection from "../../components/profiles/LabPreferenceSection";
import LabPreferenceScreenshots from "../../components/admin/LabPreferenceScreenshots";
import ConfirmModal from "../../components/utilities/confirmModal";
import { useToast } from "../../components/utilities/Toast";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  fetchLabPreferenceByToken,
  updateLabPreference,
} from "../../store/admin/labPreferenceSlice";
import {
  AdminPageShell,
  AdminPageHeader,
  AdminPrimaryButton,
} from "../../components/admin/AdminPageShell";

const defaultLabPreferences = {
  parameters: {
    occlusal_clearance: "",
    proximal_contact: "",
    cement_gap: "",
    extra_cement_gap: "",
    drill_radius: "",
    margin_parameter: "",
    pontic_design: "",
  },
  anatomy: { library: "", anatomy_preference: "" },
  library_preference: { any_preference: "", best_match: "" },
  software: { scanner_used: "", design_software: "", software_version: "" },
  modelPreference: { type: "" },
  files_preference: { files: "" },
  otherPreference: {
    natural_anatomy: "",
    ovate_pontic: "",
    read_notes_carefully: "",
  },
};

const LabPrefrences = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const { token } = useParams();
  const { user } = useAppSelector((state) => state.auth);
  const canUpload = ["Super Admin", "Admin", "Operations"].includes(
    user?.role || ""
  );

  const { labPreferences, loading } = useAppSelector(
    (state) => state.labPreference
  );

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [form, setForm] = useState<any>({
    lab_preferences: defaultLabPreferences,
  });

  useEffect(() => {
    if (token) {
      dispatch(fetchLabPreferenceByToken(token));
    }
  }, [dispatch, token]);

  useEffect(() => {
    if (labPreferences?.lab_preferences) {
      const parsed = JSON.parse(labPreferences.lab_preferences);

      setForm({
        lab_preferences: {
          ...defaultLabPreferences,
          ...parsed,
        },
      });
    }
  }, [labPreferences]);

  const handleLabPrefChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    section: string
  ) => {
    const { name, value } = e.target;

    setForm((prev: any) => ({
      ...prev,
      lab_preferences: {
        ...prev.lab_preferences,
        [section]: {
          ...prev.lab_preferences[section],
          [name]: value,
        },
      },
    }));
  };

  const submitPreference = async () => {
    if (!token) {
      showToast("Invalid or missing token", "error");
      return;
    }
    try {
      await dispatch(
        updateLabPreference({
          token,
          lab_preferences: JSON.stringify(form.lab_preferences),
        })
      ).unwrap();

      showToast("Lab preferences updated successfully", "success");
    } catch (error: any) {
      showToast(error.message || "Something went wrong", "error");
    }
  };

  return (
    <>
      <Helmet>
        <title>Lab Preferences</title>
      </Helmet>

      <AdminPageShell>
        <AdminPageHeader
          title="Lab Preferences"
          subtitle={token ? `Editing preferences for lab ${token}` : undefined}
        />

        <div className="rounded-2xl border border-[#E8E8E4] bg-white shadow-sm p-4 sm:p-6">
          {loading ? (
            <div className="py-14 text-center text-[#8A8A85]">
              <span
                className="inline-block h-8 w-8 rounded-full border-2 border-[#E8E8E4] border-t-[#2fa9bf] animate-spin"
                aria-hidden
              />
            </div>
          ) : (
            <>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setShowConfirmModal(true);
                }}
              >
                <LabPreferenceSection
                  labPreferences={form.lab_preferences}
                  handleChange={handleLabPrefChange}
                />

                <AdminPrimaryButton
                  type="submit"
                  className="mt-6 !px-6 !py-2.5 !text-sm"
                >
                  Update
                </AdminPrimaryButton>
              </form>

              {token ? (
                <LabPreferenceScreenshots token={token} canUpload={canUpload} />
              ) : null}
            </>
          )}
        </div>
      </AdminPageShell>

      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => {
          setShowConfirmModal(false);
          void submitPreference();
        }}
        title="Update Lab Preference"
        message="Are you sure you want to update Lab Preference?"
        confirmColor="blue"
        confirmLabel="Yes, Confirm"
        cancelLabel="Cancel"
      />
    </>
  );
};

export default LabPrefrences;
